import Breadcrumb from "components/Common/Breadcrumb";
import DashboardLoader from "components/Loader/DashboardLoader";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Row, Col, Card, CardBody, Button } from "reactstrap";
import {
  useGetWorkLogByIdQuery,
  useUpdateWorkLogMutation
} from "store/features/client/clientManagementApi";
import { useGetAllProjectsQuery } from "store/features/client/clientManagementApi";
import { useSelector } from "react-redux";
import { selectUser } from "store/features/auth/authSlice";
import { toast } from "sonner";

const EditWorkLog = () => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const { id } = useParams();

  const { data: workLog, isLoading } = useGetWorkLogByIdQuery(id);
  const [updateWorkLog, { isLoading: updating }] = useUpdateWorkLogMutation();
  const { data: projects = [] } = useGetAllProjectsQuery();

  const [formData, setFormData] = useState({
    project_id: "",
    title: "",
    work_summary: "",
    hours_spent: "",
    work_status: "In Progress",
    date: ""
  });

  useEffect(() => {
    if (workLog) {
      setFormData({
        project_id: workLog.project_id || "",
        title: workLog.title || "",
        work_summary: workLog.work_summary || "",
        hours_spent: workLog.hours_spent || "",
        work_status: workLog.work_status || "In Progress",
        date: workLog.date || new Date().toISOString().split('T')[0]
      });
    }
  }, [workLog]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const workLogData = {
        ...formData,
        hours_spent: parseFloat(formData.hours_spent) || 0
      };

      await updateWorkLog({ id, ...workLogData }).unwrap();
      toast.success("Work log updated successfully!");
      navigate("/worklog");
    } catch (error) {
      toast.error("Failed to update work log");
      console.error('Work log update error:', error);
    }
  };

  if (isLoading) {
    return <DashboardLoader />;
  }

  // Check if user has permission to edit
  const canEdit = user?.role === "super_admin" || user?.role === "admin" || workLog?.employer_id === user?.id;

  if (!canEdit) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="text-center py-5">
            <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
            <h4 className="text-warning">Access Denied</h4>
            <p>You don't have permission to edit this work log.</p>
            <Link to="/worklog" className="btn btn-primary">
              Back to Work Logs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb
          title="Edit Work Log"
          breadcrumbItem={
            <>
              <Link to="/worklog">Work Log</Link> / Edit Work Log
            </>
          }
        />

        <Row className="justify-content-center">
          <Col lg={8}>
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">
                  Edit Work Log - {workLog?.employer_name}
                </h4>

                <form onSubmit={handleSubmit}>
                  <Row>
                    <Col lg={6}>
                      <div className="mb-3">
                        <label className="form-label">Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="date"
                          value={formData.date}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="mb-3">
                        <label className="form-label">Project</label>
                        <select
                          className="form-select"
                          name="project_id"
                          value={formData.project_id}
                          onChange={handleChange}
                        >
                          <option value="">Select Project (Optional)</option>
                          {projects.map(project => (
                            <option key={project.id} value={project.id}>
                              {project.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </Col>
                    <Col lg={12}>
                      <div className="mb-3">
                        <label className="form-label">Title</label>
                        <input
                          type="text"
                          className="form-control"
                          name="title"
                          value={formData.title}
                          onChange={handleChange}
                          placeholder="Enter work title (optional)"
                        />
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="mb-3">
                        <label className="form-label">Hours Spent</label>
                        <input
                          type="number"
                          step="0.5"
                          min="0.5"
                          max="24"
                          className="form-control"
                          name="hours_spent"
                          value={formData.hours_spent}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="mb-3">
                        <label className="form-label">Work Status</label>
                        <select
                          className="form-select"
                          name="work_status"
                          value={formData.work_status}
                          onChange={handleChange}
                          required
                        >
                          <option value="In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                          <option value="Pending">Pending</option>
                          <option value="Blocked">Blocked</option>
                        </select>
                      </div>
                    </Col>
                    <Col lg={12}>
                      <div className="mb-3">
                        <label className="form-label">Work Summary</label>
                        <textarea
                          className="form-control"
                          name="work_summary"
                          value={formData.work_summary}
                          onChange={handleChange}
                          rows="6"
                          placeholder="Describe what you worked on..."
                          required
                        />
                      </div>
                    </Col>
                  </Row>

                  <div className="mt-4">
                    <Button
                      type="submit"
                      color="primary"
                      className="me-2"
                      disabled={updating}
                    >
                      {updating ? "Updating..." : "Update Work Log"}
                    </Button>
                    <Button
                      type="button"
                      color="light"
                      onClick={() => navigate("/worklog")}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default EditWorkLog;